import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewMyBagFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const filterByPreviewId = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const foundItem = getNestedItem(entries, ['items:sys.id'], previewConfig.previewId)

    if (previewConfig.previewId && foundItem) {
      return {
        ...entries,
        items: [foundItem],
      }
    }

    return entries
  }

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const tag = previewConfig.previewPersonalizationTag
    if (!tag || !entries.items?.length) return entries

    return {
      ...entries,
      items: entries.items.map((item) => {
        if (!item?.fields) return item
        const updatedFields = { ...item.fields }

        Object.entries(updatedFields).forEach(([fieldName, fieldValue]) => {
          // Handle array fields
          if (Array.isArray(fieldValue)) {
            updatedFields[fieldName] = fieldValue.map((item) => filterArrayItemByTag(item, tag)).filter(Boolean)
          }
          // Handle rich text content field (special case) - ie. clickAndCollectDetails
          else if (
            fieldValue &&
            typeof fieldValue === 'object' &&
            'nodeType' in fieldValue &&
            'content' in fieldValue
          ) {
            updatedFields[fieldName] = filterRichTextByPersonalizationTag(fieldValue, tag)
          }
          // Handle nested objects with fields property
          else if (fieldValue && typeof fieldValue === 'object' && 'fields' in fieldValue) {
            const entry = fieldValue as Entry
            const tags = (entry.fields?.personalizationTags as string[]) || []

            // Remove if has tags but doesn't match
            if (tags.length > 0 && !tags.includes(tag)) {
              delete processNestedFieldsByTag[fieldName]
            } else {
              // Recursively process nested fields
              processNestedFieldsByTag[fieldName] = {
                ...entry,
                fields: processNestedFieldsByTag(entry.fields, tag),
              }
            }
          }
        })
        return {
          ...item,
          fields: updatedFields,
        }
      }),
    }
  }

  const extractPanelsFromField = (fieldName: string, fieldValue): LocalePageEntry[] => {
    const panels: LocalePageEntry[] = []

    // Handle array fields - direct panel items
    if (Array.isArray(fieldValue)) {
      return extractPanelsFromArray(fieldValue)
    }

    // Handle orderInformationAccordion - object with nested items array
    if (
      fieldValue?.sys?.contentType?.sys?.id === 'accordionPanel' &&
      typeof fieldValue === 'object' &&
      fieldValue !== null
    ) {
      // Add the accordion panel itself
      panels.push(...extractPanelsFromObject(fieldValue))

      // Also extract panels from items array within the accordion
      if (Array.isArray(fieldValue?.fields?.items)) {
        fieldValue.fields.items.forEach((item) => {
          if (item?.sys?.id) {
            panels.push(item as LocalePageEntry)
          }
        })
      }

      return panels
    }

    // Handle clickAndCollectDetails - rich text with nested content.target references
    if (fieldValue?.nodeType && Array.isArray(fieldValue?.content)) {
      fieldValue.content.forEach((contentItem) => {
        // Check nested content nodes
        if (contentItem?.content && Array.isArray(contentItem.content)) {
          contentItem.content.forEach((node) => {
            if (node?.data?.target?.sys?.id) {
              panels.push(node.data.target as LocalePageEntry)
            }
          })
        }
        // Check direct target
        if (contentItem?.data?.target?.sys?.id) {
          panels.push(contentItem.data.target as LocalePageEntry)
        }
      })
      return panels
    }

    // Handle single panel fields (objects with sys.id)
    const panel = extractPanelFromValue(fieldValue)
    if (panel) panels.push(panel)

    return panels
  }

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return []

    const panels: PanelInfo[] = []
    const seenIds = new Set<string>()

    // Dynamically process all fields to extract panels
    Object.entries(firstPage).forEach(([fieldName, fieldValue]) => {
      const extractedPanels = extractPanelsFromField(fieldName, fieldValue)

      extractedPanels.forEach((panel) => {
        if (!seenIds.has(panel.sys.id)) {
          seenIds.add(panel.sys.id)
          panels.push(getPanelInfo(panel))
        }
      })
    })

    return panels
  }

  const filterRichTextContent = (value, panelDisplayInfo: Record<string, { show: boolean | string }>) => {
    if (!Array.isArray(value?.content)) return value

    return {
      ...value,
      content: value.content
        .map((contentItem) => {
          // Handle content items with nested content nodes
          if (contentItem?.content && Array.isArray(contentItem.content)) {
            return {
              ...contentItem,
              content: filterContentByDisplayInfo(contentItem.content, panelDisplayInfo),
            }
          }
          return contentItem
        })
        .filter((contentItem) => {
          // Filter out items where target is hidden
          if (contentItem?.data?.target?.sys?.id) {
            const targetId = contentItem.data.target.sys.id
            return !targetId || panelDisplayInfo[targetId]?.show !== false
          }
          return true
        }),
    }
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
  ) => {
    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return entries

    // Dynamically process all fields
    Object.entries(firstPage).forEach(([key, value]) => {
      // Handle array fields
      if (Array.isArray(value)) {
        firstPage[key] = filterArrayByDisplayInfo(value, panelDisplayInfo)
      }
      // Handle rich text content
      else if (value?.nodeType && value?.content) {
        firstPage[key] = filterRichTextContent(value, panelDisplayInfo)
      }
      // Handle single panel fields (objects with sys.id)
      else if (value && typeof value === 'object' && value?.sys?.id) {
        const itemId = extractPanelId(value)
        const shouldShow = !itemId || panelDisplayInfo[itemId]?.show !== false
        if (!shouldShow) {
          delete firstPage[key]
        }
      }
    })

    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    filterByPreviewId,
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
